import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ButtonT1 } from "../../../../../components/atoms/Button";
import { useGetOperationsNoteTargetsQuery } from "../../../../../store/apiSlice/operationsApiSlice";
import {
  Chip,
  ChipRow,
  ComposerActions,
  ComposerCard,
  ComposerFooter,
  ComposerShell,
  ComposerTop,
  FieldLabel,
  Hint,
  SaveHint,
  TaskOptions,
  TextArea,
} from "./NotesComposer.styles";
import {
  activeDueShortcut,
  DUE_SHORTCUTS,
  formatDueDate,
  parseNoteCommands,
  resolveDuePhrase,
  toIsoDate,
} from "./parseNoteCommands";
import { activeMention } from "./taskMentions";

function sameRecord(left, right) {
  return left?.type === right?.type && left?.id === right?.id;
}

function recordTypeLabel(type) {
  if (type === "purchase_order") return "Purchase order";
  if (type === "transfer_order") return "Transfer order";
  return "Shipment";
}

function initialRecords(defaultRecord, editingItem) {
  if (editingItem?.records?.length) return editingItem.records;
  if (editingItem?.primary_record) return [editingItem.primary_record];
  if (defaultRecord) return [defaultRecord];
  return [];
}

const MentionField = styled.div`
  position: relative;
`;
const MentionMenu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(23, 30, 45, 0.14);

  button {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    border: 0;
    border-radius: 4px;
    padding: 9px 10px;
    background: #fff;
    color: var(--GREY, #4a4a68);
    font:
      13px Inter,
      sans-serif;
    text-align: left;
    cursor: pointer;
  }
  button[aria-selected="true"],
  button:hover {
    background: var(--MIST, #f7f8f9);
  }
  button span {
    min-width: 100px;
    color: var(--ASH, #676879);
  }
  button strong {
    color: var(--DARK-GREY, #0e0e2c);
  }
`;
const MentionTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  margin-right: 8px;
  border-radius: 4px;
  padding: 5px 8px;
  background: var(--GHOST, #efeffd);
  color: var(--DARK-BLUE, #1249bf);
  font:
    600 12px/1.3 Inter,
    sans-serif;
  button {
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 17px;
    line-height: 1;
    cursor: pointer;
  }
`;
const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

export function TaskComposer({
  defaultRecord = null,
  editingItem = null,
  onSave,
  onClear,
  saving = false,
}) {
  const [currentText, setCurrentText] = useState("");
  const [records, setRecords] = useState(() => initialRecords(defaultRecord, null));
  const [mention, setMention] = useState(null);
  const [dueShortcut, setDueShortcut] = useState(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [pickerDueDate, setPickerDueDate] = useState(null);
  const [includeDueTime, setIncludeDueTime] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const textAreaRef = useRef(null);
  const isEditing = Boolean(editingItem);
  const dueShortcutComplete =
    dueShortcut && DUE_SHORTCUTS.some(item => item.key === dueShortcut.typed);
  const dueMenuOpen =
    Boolean(dueShortcut) && !dueShortcutComplete && (dueShortcut.options?.length ?? 0) > 0;
  const recordMenuOpen = Boolean(mention) && !dueMenuOpen;
  const { data: targets, isFetching: targetsLoading } = useGetOperationsNoteTargetsQuery(
    mention?.search || "",
    { skip: !recordMenuOpen },
  );
  const mentionOptions = (targets?.records || [])
    .filter(item => !records.some(record => sameRecord(record, item)))
    .filter(
      item =>
        !mention?.type ||
        (mention.type === "shipment"
          ? item.type === "shipment" || item.type === "consignment"
          : item.type === mention.type),
    );
  const taskText = currentText.trim();
  const parsed = parseNoteCommands(taskText, { pickerDueDate });

  useEffect(() => {
    setCurrentText(editingItem?.content || "");
    setRecords(initialRecords(defaultRecord, editingItem));
    setMention(null);
    setDueShortcut(null);
    setPickerDueDate(editingItem?.due_date ? dayjs(editingItem.due_date) : null);
    setIncludeDueTime(false);
    setSaveError(false);
    textAreaRef.current?.focus();
    textAreaRef.current?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem?.id, defaultRecord?.id, defaultRecord?.type, defaultRecord?.name]);

  const clearDraft = () => {
    setCurrentText("");
    setRecords(initialRecords(defaultRecord, null));
    setMention(null);
    setDueShortcut(null);
    setSelectedMentionIndex(0);
    setPickerDueDate(null);
    setIncludeDueTime(false);
    setSaveError(false);
    onClear?.();
  };

  const save = async () => {
    if (!parsed.content || saving) return;
    const body = {
      content: parsed.isTask ? taskText : `${taskText} /task`,
      due_date: toIsoDate(pickerDueDate) || toIsoDate(parsed.dueDate),
    };
    try {
      await onSave({ records, body, editingItem });
      clearDraft();
    } catch {
      setSaveError(true);
    }
  };

  const changeText = event => {
    const text = event.target.value;
    const nextParsed = parseNoteCommands(text);
    if (nextParsed.hasDueToken && nextParsed.dueDate) {
      setPickerDueDate(dayjs(nextParsed.dueDate));
    }
    setCurrentText(text);
    const cursor = event.target.selectionStart;
    const nextDue = activeDueShortcut(text, cursor);
    const dueComplete = nextDue && DUE_SHORTCUTS.some(item => item.key === nextDue.typed);
    const nextDueMenuOpen =
      Boolean(nextDue) && !dueComplete && (nextDue.options?.length ?? 0) > 0;
    setDueShortcut(dueComplete ? null : nextDue);
    setMention(nextDueMenuOpen ? null : activeMention(text, cursor));
    setSelectedMentionIndex(0);
  };

  const chooseMention = item => {
    if (!mention) return;
    const token = `@${item.name}`;
    setCurrentText(
      `${currentText.slice(0, mention.start)}${token}${currentText.slice(mention.end)}`.replace(
        /[ \t]{2,}/g,
        " ",
      ),
    );
    setRecords(previous =>
      previous.some(record => sameRecord(record, item)) ? previous : [...previous, item],
    );
    setMention(null);
    setDueShortcut(null);
    textAreaRef.current?.focus();
  };

  const chooseDueShortcut = item => {
    if (!dueShortcut) return;
    setCurrentText(
      `${currentText.slice(0, dueShortcut.start)}/${item.key}${currentText.slice(dueShortcut.end)}`,
    );
    setPickerDueDate(dayjs(resolveDuePhrase(item.key, new Date())));
    setDueShortcut(null);
    setMention(null);
    textAreaRef.current?.focus();
  };

  const chipRecords = records.filter(record => !currentText.includes(`@${record.name}`));
  const shortcutMenu = dueMenuOpen ? dueShortcut : mention;
  const shortcutOptions = dueMenuOpen
    ? (dueShortcut?.options || []).map(item => ({
        key: item.key,
        label: "Due",
        name: item.label,
        onChoose: () => chooseDueShortcut(item),
      }))
    : recordMenuOpen
    ? mentionOptions.map(item => ({
        key: `${item.type}-${item.id}`,
        label: recordTypeLabel(item.type),
        name: item.name,
        onChoose: () => chooseMention(item),
      }))
    : [];

  const removeRecord = record => {
    setRecords(previous => previous.filter(item => !sameRecord(item, record)));
  };

  return (
    <ComposerShell>
      {isEditing && (
        <ComposerTop>
          <FieldLabel htmlFor="task-composer-input">Edit task</FieldLabel>
        </ComposerTop>
      )}
      {showShortcuts && (
        <Hint style={{ marginBottom: 10 }}>
          Type <code>@sh</code> or <code>shipment 123</code> to tag a shipment, and <code>@po</code>{" "}
          or <code>po 123</code> for a purchase order. Type <code>/tomorrow</code> to set a due
          date.
        </Hint>
      )}
      <MentionField>
        <ComposerCard>
          <TextArea
            id="task-composer-input"
            aria-label="Task description"
            ref={textAreaRef}
            value={currentText}
            onChange={changeText}
            autoFocus
            placeholder="Big things start with small tasks..."
            onKeyDown={event => {
              if (shortcutMenu && event.key === "ArrowDown") {
                event.preventDefault();
                setSelectedMentionIndex(index =>
                  Math.min(index + 1, Math.max(shortcutOptions.length - 1, 0)),
                );
              } else if (shortcutMenu && event.key === "ArrowUp") {
                event.preventDefault();
                setSelectedMentionIndex(index => Math.max(index - 1, 0));
              } else if (shortcutMenu && event.key === "Escape") {
                event.preventDefault();
                setMention(null);
                setDueShortcut(null);
              } else if (shortcutMenu && event.key === "Enter" && shortcutOptions.length) {
                event.preventDefault();
                (shortcutOptions[selectedMentionIndex] || shortcutOptions[0]).onChoose();
              } else if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                save();
              }
            }}
          />
          <ComposerFooter>
            <SaveHint>
              Enter to save
              {" · "}
              <button
                type="button"
                aria-expanded={showShortcuts}
                onClick={() => setShowShortcuts(open => !open)}
                style={{
                  border: 0,
                  background: "transparent",
                  color: "var(--DARK-BLUE, #1249bf)",
                  font: "600 12px/1 Inter, sans-serif",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Shortcuts
              </button>
            </SaveHint>
            <ComposerActions>
              {(currentText || isEditing || records.length) && (
                <ButtonT1
                  title="Clear"
                  $variant="secondary"
                  $size="small"
                  $height={28}
                  onClickCallback={clearDraft}
                />
              )}
              <ButtonT1
                title="Save"
                $size="small"
                $height={28}
                onClickCallback={save}
                disabled={!parsed.content || saving}
              />
            </ComposerActions>
          </ComposerFooter>
        </ComposerCard>
        {shortcutMenu && (
          <MentionMenu
            role="listbox"
            aria-label={dueMenuOpen ? "Set a due date" : "Tag a shipment or purchase order"}
          >
            {recordMenuOpen && targetsLoading && <span>Searching…</span>}
            {recordMenuOpen && !targetsLoading && !shortcutOptions.length && (
              <span>No matching records</span>
            )}
            {dueMenuOpen && !shortcutOptions.length && <span>No matching due date</span>}
            {!(recordMenuOpen && targetsLoading) &&
              shortcutOptions.map((item, index) => (
                <button
                  key={item.key}
                  type="button"
                  role="option"
                  aria-selected={index === selectedMentionIndex}
                  onMouseDown={event => event.preventDefault()}
                  onClick={item.onChoose}
                >
                  <span>{item.label}</span>
                  <strong>{item.name}</strong>
                </button>
              ))}
          </MentionMenu>
        )}
      </MentionField>
      {chipRecords.length > 0 && (
        <TagRow>
          {chipRecords.map(record => (
            <MentionTag key={`${record.type}-${record.id}`}>
              Tagged: {recordTypeLabel(record.type)} {record.name}
              <button
                type="button"
                aria-label={`Remove ${record.name}`}
                onClick={() => removeRecord(record)}
              >
                ×
              </button>
            </MentionTag>
          ))}
        </TagRow>
      )}
      <TaskOptions>
        <div>
          <FieldLabel>Due date</FieldLabel>
          <ChipRow>
            {["today", "tomorrow", "friday", "next week"].map(label => (
              <Chip
                key={label}
                type="button"
                $active={
                  toIsoDate(pickerDueDate) === toIsoDate(resolveDuePhrase(label, new Date()))
                }
                onClick={() => {
                  const date = dayjs(resolveDuePhrase(label, new Date()));
                  setPickerDueDate(
                    includeDueTime
                      ? date.hour(9).minute(0).second(0)
                      : date.hour(0).minute(0).second(0).millisecond(0),
                  );
                }}
              >
                {label === "next week"
                  ? "Next week"
                  : label === "friday"
                    ? "Friday"
                    : label === "today"
                      ? "Today"
                      : "Tomorrow"}
              </Chip>
            ))}
          </ChipRow>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              color: "var(--GREY, #4a4a68)",
              font: "500 12px/1.4 Inter, sans-serif",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={includeDueTime}
              onChange={event => {
                const checked = event.target.checked;
                setIncludeDueTime(checked);
                setPickerDueDate(current => {
                  if (!current) return current;
                  return checked
                    ? current.hour(9).minute(0).second(0)
                    : current.startOf("day");
                });
              }}
            />
            Add time
          </label>
          <DatePicker
            value={pickerDueDate}
            onChange={value => {
              if (!value) {
                setPickerDueDate(null);
                return;
              }
              setPickerDueDate(
                includeDueTime ? value : value.hour(0).minute(0).second(0).millisecond(0),
              );
            }}
            showTime={
              includeDueTime
                ? { format: "HH:mm", minuteStep: 15, showNow: false, hideDisabledOptions: true }
                : false
            }
            format={includeDueTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY"}
            placeholder={includeDueTime ? "Pick a date and time" : "Pick a date"}
            allowClear
            style={{ marginTop: 7, width: "100%" }}
            getPopupContainer={() => document.body}
          />
          {(pickerDueDate || parsed.dueDate) && (
            <Hint style={{ marginTop: 5 }}>
              Due {formatDueDate(pickerDueDate || parsed.dueDate, { includeTime: includeDueTime })}
            </Hint>
          )}
        </div>
      </TaskOptions>
      {saveError && <p role="alert">Could not save the task. Please try again.</p>}
    </ComposerShell>
  );
}
